<template>
  <article class="mpc" :class="{ 'mpc--row': row, 'is-on': active }" :data-doctor-id="doctor.id" data-testid="map-pin-card">
    <img class="mpc__avatar" :src="doctor.avatar || placeholder" alt="" />
    <div class="mpc__body">
      <h3 class="mpc__name">{{ doctor.name }}</h3>
      <p v-if="doctor.specialities" class="mpc__spec">{{ doctor.specialities }}</p>
      <p class="mpc__next" aria-live="polite" data-testid="map-pin-next">
        <template v-if="!next || next.status === 'idle' || next.status === 'loading'">
          <span class="mpc__label">Next free</span> <span class="mpc__muted">Checking times...</span>
        </template>
        <template v-else-if="next.status === 'done' && next.slots.length">
          <span class="mpc__label">Next free</span>
          <strong class="mpc__day">{{ dayText }}</strong>
        </template>
        <span v-else class="mpc__muted">{{ next.status === 'error' ? 'Times could not load' : 'No free times in the next few days' }}</span>
      </p>
      <SlotCard
        v-if="next && next.status === 'done' && next.slots.length"
        :selectable="!row"
        state="free"
        :tag="row ? 'div' : ''"
        :start="next.slots[0].startTime"
        :end="next.slots[0].endTime"
        :type="next.slots[0].type"
        :price="next.slots[0].perSlotAmount"
        :data-slot-id="next.slots[0].id"
        @click.stop="!row && $emit('open', { date: next.date, slot: next.slots[0].id })"
      />
      <div v-if="!row" class="mpc__foot">
        <span v-if="doctor.price" class="mpc__price">From <strong>{{ doctor.price }}</strong></span>
        <span v-else></span>
        <button type="button" class="mpc__view" data-testid="map-pin-view" @click.stop="$emit('open', {})">View profile</button>
      </div>
    </div>
  </article>
</template>

<script>
// TB-109-C: the card shown on a search map pin (and, as a row, in the list beside
// the map). Same Clinical Calm look as DoctorResultCard; text only, no v-html.
import { dayLabel } from '@/utils/slotPicker'
import SlotCard from '@/components/slot/SlotCard.vue'

export default {
  name: 'MapPinCard',
  components: { SlotCard },
  props: {
    // DoctorResultCard shape: { id, name, specialities, avatar, price }
    doctor: { type: Object, required: true },
    // { status: idle|loading|done|error, date, slots[] }
    next: { type: Object, default: null },
    row: { type: Boolean, default: false },
    active: { type: Boolean, default: false }
  },
  emits: ['open'],
  data () {
    return { placeholder: 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png' }
  },
  computed: {
    dayText () {
      const n = this.next
      return n && n.slots && n.slots.length ? dayLabel(n.date) : ''
    }
  },
  methods: {
  }
}
</script>

<style scoped>
.mpc { display: flex; gap: 0.75rem; align-items: flex-start; width: 17.5rem; max-width: 100%; padding: 0.25rem; font-family: inherit; color: var(--tb-text-body); }
.mpc__avatar { width: 3rem; height: 3rem; border-radius: 50%; object-fit: cover; flex: none; box-shadow: var(--tb-shadow-sm); }
.mpc__body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
.mpc__name { margin: 0; font-size: 0.95rem; font-weight: 800; color: var(--tb-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.mpc__spec { margin: 0; font-size: 0.8rem; color: var(--tb-text-muted); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.mpc__next { margin: 0.25rem 0 0; font-size: 0.8rem; display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; }
.mpc__label { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; color: var(--tb-text-muted); }
.mpc__muted { color: var(--tb-text-muted); }
.mpc__foot { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-top: 0.5rem; }
.mpc__price { font-size: 0.78rem; color: var(--tb-text-muted); }
.mpc__price strong { color: var(--tb-text-primary); }
.mpc__view {
  padding: 0.4rem 1rem; border-radius: 999px; border: 0;
  background: var(--tb-primary); color: var(--tb-primary-contrast); font: inherit; font-weight: 800; font-size: 0.8rem; cursor: pointer;
}
.mpc__view:hover { background: var(--tb-primary-hover); }
.mpc__day { color: var(--tb-text-primary); }
.mpc__view:focus-visible { outline: none; box-shadow: var(--tb-shadow-focus); }

.mpc--row { width: 100%; padding: 0.75rem; border-radius: var(--tb-radius-control); border: 1px solid transparent; cursor: pointer; transition: background 0.15s ease, border-color 0.15s ease; }
.mpc--row:hover { background: var(--tb-bg-subtle); }
.mpc--row.is-on { background: var(--tb-primary-container); border-color: var(--tb-primary); }
.mpc--row .mpc__avatar { width: 2.5rem; height: 2.5rem; }
@media (prefers-reduced-motion: reduce) { .mpc--row { transition: none; } }
</style>
